import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import {
  BILLING_ACCOUNT_STATUS_LINKED,
  getBillingAccountStatusConfig,
} from "@/v2-backoffice-client/customer/billing/customerBilling.constants"

export type CustomerBillingBadgeProps = {
  customer: Pick<
    CustomerFragment,
    "BillingAccountStatus" | "BillingCompanyName"
  >
}

// CustomerBillingBadge surfaces how a customer relates to its Fenerum billing
// account (server-derived on the customer row) — the billing sibling of
// CustomerCompanyBadge. The healthy Linked state renders as a plain dot with
// the account name; every other state gets a tooltip explaining it and naming
// the fix.
function CustomerBillingBadge(props: CustomerBillingBadgeProps) {
  const { customer } = props

  const status = customer.BillingAccountStatus
  const config = getBillingAccountStatusConfig(status)
  if (!config) return null

  if (status === BILLING_ACCOUNT_STATUS_LINKED) {
    return <DotText variant={config.variant}>{config.label}</DotText>
  }

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant={config.variant}>{config.label}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-1 p-3">
          <DotText
            variant={config.variant}
            className="flex font-medium text-popover-foreground"
          >
            {config.label}
          </DotText>
          <p className="text-muted-foreground">{config.description}</p>

          {customer.BillingCompanyName && (
            <p className="mt-1 text-xs">
              <span className="text-muted-foreground">Fenerum account: </span>
              {customer.BillingCompanyName}
            </p>
          )}

          {config.fix && (
            <p className="mt-1 text-xs text-muted-foreground">{config.fix}</p>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default CustomerBillingBadge
